import { MessagesGetRequest } from 'Interfaces/apiDtos';
import { IConversationModel } from 'Models/ConversationModel';
import { RootStore } from 'Stores/RootStore';
import { RootStoreProps } from 'Stores/RootStore.types';
import { isNullOrUndefined } from 'Utils/typeGuards';

const getMessageStore = (rootStore: RootStore | RootStoreProps) =>
  (rootStore as RootStore).messageStore ??
  (rootStore as RootStoreProps).message;

const getPreferenceStore = (rootStore: RootStore | RootStoreProps) =>
  (rootStore as RootStore).preferenceStore ??
  (rootStore as RootStoreProps).preference;

/**
 * Refresh stale message cache in the background while keeping existing messages visible.
 * @param conversation - The conversation to check
 * @param rootStore - The root store containing message store
 */
export const refreshStaleMessageCache = (
  conversation: IConversationModel,
  rootStore: RootStore | RootStoreProps
) => {
  const messageStore = getMessageStore(rootStore);
  const preferenceStore = getPreferenceStore(rootStore);

  const conversationId = String(conversation.id);

  if (!messageStore.messageByConvStatusMap.has(conversationId)) {
    return;
  }

  const cachedMessages =
    messageStore.groupedMessagesByConversationMap.get(conversationId);

  const cacheIsStale =
    cachedMessages &&
    !isNullOrUndefined(conversation.lastMessageId) &&
    !isNullOrUndefined(cachedMessages.NewestMessageId) &&
    conversation.lastMessageId !== cachedMessages.NewestMessageId;

  if (!cacheIsStale) {
    return;
  }

  messageStore.messageByConvStatusMap.delete(conversationId);

  const newestMessageId =
    cachedMessages.NewestMessageId ?? cachedMessages.NewestMessage?.id;

  if (!isNullOrUndefined(newestMessageId)) {
    void messageStore.loadMessagesSinceNewestMessage(
      conversationId,
      newestMessageId
    );
    return;
  }

  const msgsGet: MessagesGetRequest = {
    Limit: 31,
    ShowCallMessagesInChat:
      preferenceStore?.preferences?.showCallMessagesInChat,
    ShowDeleted: true,
    SortDirection: 'Descending',
  };

  void messageStore.loadConversationMessages(conversationId, msgsGet);
};

/**
 * Invalidate if the cache is actually stale.
 */
export const invalidateStaleMessageCache = (
  conversation: IConversationModel,
  rootStore: RootStore | RootStoreProps
) => {
  refreshStaleMessageCache(conversation, rootStore);
};

/**
 * Clear all message caches to force fresh data fetch.
 * Used for hard resets (e.g. logout), not for network recovery.
 * @param rootStore - The root store containing message store
 */
export const clearAllMessageCaches = (
  rootStore: RootStore | RootStoreProps
) => {
  const messageStore = getMessageStore(rootStore);

  messageStore.messageByConvStatusMap.clear();
  messageStore.groupedMessagesByConversationMap.clear();
};
